import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScoreBar } from './ScoreBar';
import { CategoryIndicators } from './CategoryIndicators';
import type { BrandOption } from '../completeness.api';

interface BrandCardProps {
  brand: BrandOption;
  onClick?: (brand: BrandOption) => void;
}

export function BrandCard({ brand, onClick }: BrandCardProps) {
  const { t } = useTranslation();
  const [imgError, setImgError] = useState(false);

  return (
    <div
      className="brand-card flex cursor-pointer flex-col items-center gap-3 rounded-xl p-5 transition-all duration-150"
      style={{
        backgroundColor: 'var(--background)',
        border: '1px solid var(--border-soft)',
      }}
      onClick={() => onClick?.(brand)}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--secondary)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(47, 90, 122, 0.08)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--border-soft)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div
        className="flex items-center justify-center rounded-[10px]"
        style={{
          width: 72,
          height: 72,
          backgroundColor: 'var(--primary-faint)',
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        {brand.logoUrl && !imgError ? (
          <img
            src={brand.logoUrl}
            alt={brand.name}
            style={{ width: 56, height: 56, objectFit: 'contain' }}
            onError={() => setImgError(true)}
          />
        ) : (
          <span
            className="text-[0.75rem] font-bold uppercase leading-tight"
            style={{
              color: 'var(--primary)',
              letterSpacing: '0.02em',
              textAlign: 'center',
              padding: '4px',
              wordBreak: 'break-word',
            }}
          >
            {brand.name.slice(0, 3)}
          </span>
        )}
      </div>
      <div
        className="text-center text-[0.8125rem] font-semibold leading-tight"
        style={{ color: 'var(--foreground)' }}
      >
        {brand.name}
      </div>
      <div className="flex w-full flex-col items-center gap-2">
        <span className="text-xs" style={{ color: 'var(--foreground-muted)' }}>
          <strong style={{ fontWeight: 600, color: 'var(--foreground)' }}>
            {brand.productCount}
          </strong>{' '}
          {t('products.brandProducts')}
        </span>
        <ScoreBar score={brand.score} />
        {brand.categoryScores && brand.categoryScores.length > 0 && (
          <div className="mt-1 flex justify-center">
            <CategoryIndicators categories={brand.categoryScores} />
          </div>
        )}
      </div>
    </div>
  );
}
